import { Divider, Grid, Link, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import type { Dayjs } from "dayjs";
import { isFinite, sortBy } from "lodash";
import type { FC } from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { usePackageWithId } from "../../admin/packages/usePackageWithId";
import type { ProductResponse } from "../../admin/products/productsApi";
import { useProducts } from "../../admin/products/useProducts";
import { isPlaneTicket } from "../../admin/providers/isPlaneTicket";
import type { UserContext } from "../../auth/auth";
import { AssociatedFilesView } from "../../components/associatedFiles/AssociatedFilesView";
import DatePicker from "../../components/datePicker/DatePickerV2";
import { InfoEdit } from "../../components/editDialog/InfoEdit";
import NumberField from "../../components/numberField/NumberField";
import type { Currency } from "../../data/BaseEntity";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import { removeArr, updateArr } from "../../misc/utils";
import type { Package } from "../../sharedComponents/packages/PackageSelect";
import PackageSelect from "../../sharedComponents/packages/PackageSelect";
import type { CalculationProduct, CalculationVersion } from "../calculationTypes";
import { autoFillCalculation, getDateRelativeTo, initializeCalculationFromPackage } from "../calculationUtils";
import CalculationPackageProducts from "./CalculationPackageProducts";
import ProductsEdit from "./ProductsEdit";
import type { PricesByDatesProductsAndOptions } from "./useProductsSellingPrices";
import { useProductsSellingPrices } from "./useProductsSellingPrices";
import { useProductsWithPrices } from "./useProductsWithPrices";

interface PackageEditProps {
  readonly packageId: string;
  readonly isCustomer: boolean;
  readonly startDate: Dayjs;
  readonly totalParticipantsNumber: number;
  readonly paidParticipantsNumber: number;
  readonly products: CalculationProduct[];
  readonly availableProducts: ProductResponse[];
  readonly prices: PricesByDatesProductsAndOptions;
  readonly onChange: (change: {
    packageId?: string;
    products?: CalculationProduct[];
    currency?: Currency;
    margin?: number;
  }) => void;
  readonly onReplaceProduct: (productId: string, replacement: CalculationProduct) => void;
}

const PackageEdit: FC<PackageEditProps> = ({
  packageId,
  isCustomer,
  startDate,
  totalParticipantsNumber,
  paidParticipantsNumber,
  products,
  availableProducts,
  prices,
  onChange,
  onReplaceProduct,
}) => {
  const { data: selectedPackage } = usePackageWithId(packageId);
  const { files = [], margin = 0, currency, note, days: packageDays = [] } = selectedPackage ?? {};
  const { productsWithPrices } = useProductsWithPrices(products, currency, margin, availableProducts, prices);
  const handleSelectPackage = (selectedPackage: Package | undefined) => {
    if (!selectedPackage) {
      return;
    }
    onChange(initializeCalculationFromPackage(selectedPackage, startDate));
  };
  const packageAttachments = files.map((f) => ({ ...f, name: "Opis pakietu" })).slice(0, 1) ?? [];
  const selectedProducts = productsWithPrices.map((p) => ({
    ...p,
    optional: packageDays.flatMap((d) => d.products).find((pp) => pp.id === p.id)?.optional,
  }));

  const handleProductChange = (p: CalculationProduct, idx: number) => {
    const previousProductId = products[idx]?.id;
    const isNewProduct = !previousProductId && p.id;
    const options = isNewProduct ? p.options.map((o) => ({ ...o, quantity: 0 })) : p.options;

    onChange({
      products: sortBy(updateArr(products, idx, { ...p, options }), (prd) => prd.date),
    });
  };
  const handleProductDelete = (idx: number) => {
    onChange({ products: removeArr(products, idx) });
  };

  return (
    <>
      <Grid size={{ xs: 7, lg: 6 }}>
        <PackageSelect packageId={packageId} onChange={handleSelectPackage} />
      </Grid>
      {packageAttachments.length > 0 && (
        <Grid
          size={2}
          container
          sx={{
            alignItems: "center",
          }}
        >
          <AssociatedFilesView values={packageAttachments} />
        </Grid>
      )}
      {note && (
        <Grid size={12}>
          <Typography variant="caption">Informacje dotyczące pakietu</Typography>
          <Typography variant="body2">{note}</Typography>
        </Grid>
      )}
      <Grid size={12}>
        <CalculationPackageProducts
          isCustomer={isCustomer}
          selectedProducts={selectedProducts}
          availableProducts={availableProducts}
          calculationCurrency={currency}
          margin={margin}
          totalParticipantsNumber={totalParticipantsNumber}
          paidParticipantsNumber={paidParticipantsNumber}
          onChange={handleProductChange}
          onDelete={handleProductDelete}
          onReplaceProduct={onReplaceProduct}
        />
      </Grid>
    </>
  );
};

enum Mode {
  PACKAGE,
  PRODUCTS,
  PLANE_TICKETS,
  A_AND_O,
}

interface Props {
  readonly calculationVersion: CalculationVersion;
  readonly userContext: UserContext;
  readonly productCurrencyErrors: string[];
  readonly currencyError: boolean;
  readonly onChange: (calculationVersion: CalculationVersion) => void;
}

function planeTicketsOnly(products: ProductResponse[]): ProductResponse[] {
  return products.filter((p) => p.provider.productType && isPlaneTicket(p.provider.productType));
}

function aAndOOnly(products: ProductResponse[]): ProductResponse[] {
  return products.filter((p) => p.provider.name?.toUpperCase().includes("A&O"));
}

const CalculationVersionEdit: FC<Props> = ({
  calculationVersion,
  userContext,
  productCurrencyErrors,
  currencyError,
  onChange,
}) => {
  const versionRef = useRef(calculationVersion);
  versionRef.current = calculationVersion;

  const { products: allProducts } = useProducts();
  const products = useMemo(
    () =>
      calculationVersion.currency ? allProducts.filter((p) => p.currency == calculationVersion.currency) : allProducts,
    [allProducts, calculationVersion.currency],
  );
  const priceQueryInput = useMemo(
    () =>
      calculationVersion.products
        .filter((p) => !!p.id)
        .map(({ id, date }) => ({
          id,
          dateFrom: date,
          dateTo: dayjs(date, ISO_DATE_FORMAT).add(1, "day").format(ISO_DATE_FORMAT),
        })),
    [
      calculationVersion.participantsNumber.numberOfPaid,
      calculationVersion.participantsNumber.numberOfStaff,
      calculationVersion.products.map((p) => `${p.id}\t${p.date}`).join(","),
    ],
  );
  const totalParticipants =
    calculationVersion.participantsNumber.numberOfPaid + calculationVersion.participantsNumber.numberOfStaff;
  const { prices } = useProductsSellingPrices(priceQueryInput, totalParticipants);
  const [mode, setMode] = useState(
    calculationVersion.products.length > 0 && !calculationVersion.packageId ? Mode.PRODUCTS : Mode.PACKAGE,
  );

  const handleChange = useCallback(
    (change: Partial<CalculationVersion>) => {
      onChange({ ...versionRef.current, ...change });
    },
    [onChange],
  );

  const handleModeChange = useCallback(
    (newMode: Mode) => {
      function overrides(): Partial<CalculationVersion> {
        if (newMode === Mode.PRODUCTS || newMode === Mode.PLANE_TICKETS || newMode === Mode.A_AND_O) {
          return { currency: null, margin: 0 };
        }
        return {};
      }
      onChange({
        ...versionRef.current,
        products: [],
        packageId: undefined,
        currency: undefined,
        margin: undefined,
        ...overrides(),
      });
      setMode(newMode);
    },
    [onChange],
  );

  const handleStartDateChange = useCallback(
    (startDate: Dayjs | null) => {
      const version = versionRef.current;
      handleChange({
        startDate: startDate?.format(ISO_DATE_FORMAT),
        products: version.products.map((p) => {
          const daysShift = isFinite(p.daysShift)
            ? (p.daysShift ?? 0)
            : dayjs(p.date, ISO_DATE_FORMAT).diff(version.startDate, "days");
          return { ...p, daysShift, date: getDateRelativeTo(startDate ?? dayjs(), daysShift) };
        }),
      });
    },
    [handleChange],
  );

  const handleReplaceProduct = useCallback(
    (productId: string, replacement: CalculationProduct) => {
      handleChange({
        products: versionRef.current.products.map((p) => (p.id === productId ? replacement : p)),
      });
    },
    [handleChange],
  );

  const handleAutoFill = useCallback(() => {
    const { products, participantsNumber } = versionRef.current;
    const totalParticipants = participantsNumber.numberOfPaid + participantsNumber.numberOfStaff;
    const updatedProducts = autoFillCalculation(products, totalParticipants, allProducts);
    handleChange({ products: updatedProducts });
  }, [handleChange, allProducts]);

  const isCustomer = userContext?.role === "CUSTOMER";
  const canCreateFromProducts =
    userContext?.role !== "CUSTOMER" || userContext?.customPrivileges.canCreateCalculationFromProducts;
  return (
    <Grid size={12} container spacing={2}>
      <Grid size={{ xs: 6, lg: 4 }}>
        <TextField
          label="Nazwa"
          value={calculationVersion.name}
          fullWidth
          autoFocus
          onChange={(e) => handleChange({ name: e.target.value })}
        />
      </Grid>
      <Grid size={{ xs: 6, lg: 4 }}>
        <DatePicker
          label="Data rozpoczęcia"
          fullWidth
          value={dayjs(calculationVersion.startDate, ISO_DATE_FORMAT)}
          onChange={(v) => handleStartDateChange(v)}
        />
      </Grid>
      <Grid size={{ xs: 3, lg: 2 }}>
        <NumberField
          label="Liczba osób płatnych"
          fullWidth
          min={1}
          max={1000}
          value={calculationVersion.participantsNumber.numberOfPaid}
          onChange={(numberOfPaid) =>
            handleChange({
              participantsNumber: { ...calculationVersion.participantsNumber, numberOfPaid: numberOfPaid ?? 0 },
            })
          }
          helperText={
            <Link component="button" variant="caption" onClick={handleAutoFill}>
              Automatycznie uzupełnij kalkulację
            </Link>
          }
        />
      </Grid>
      <Grid size={{ xs: 3, lg: 2 }}>
        <NumberField
          label="Liczba osób obsługi"
          fullWidth
          min={0}
          max={1000}
          value={calculationVersion.participantsNumber.numberOfStaff}
          onChange={(numberOfStaff) =>
            handleChange({
              participantsNumber: { ...calculationVersion.participantsNumber, numberOfStaff: numberOfStaff ?? 0 },
            })
          }
        />
      </Grid>
      <Grid size={12}>
        <InfoEdit
          label="Dodatkowe informacje"
          value={calculationVersion.note}
          onChange={(note) => handleChange({ note })}
        />
      </Grid>
      <Grid size={12}>
        <Divider />
      </Grid>
      <Grid size={{ xs: 5, lg: 4 }}>
        <ToggleButtonGroup exclusive value={mode} onChange={(_, newValue) => handleModeChange(newValue)}>
          <ToggleButton size="large" value={Mode.PACKAGE}>
            Pakiet
          </ToggleButton>
          {canCreateFromProducts && (
            <ToggleButton size="large" value={Mode.PRODUCTS}>
              Produkty
            </ToggleButton>
          )}
          <ToggleButton size="large" value={Mode.PLANE_TICKETS}>
            Bilety lotnicze
          </ToggleButton>
          <ToggleButton size="large" value={Mode.A_AND_O}>
            A&O
          </ToggleButton>
        </ToggleButtonGroup>
      </Grid>
      {mode === Mode.PACKAGE && (
        <PackageEdit
          packageId={calculationVersion.packageId ?? ""}
          isCustomer={isCustomer}
          startDate={dayjs(calculationVersion.startDate, ISO_DATE_FORMAT)}
          totalParticipantsNumber={totalParticipants}
          paidParticipantsNumber={calculationVersion.participantsNumber.numberOfPaid}
          products={calculationVersion.products}
          availableProducts={products}
          prices={prices}
          onChange={handleChange}
          onReplaceProduct={handleReplaceProduct}
        />
      )}
      {mode === Mode.PRODUCTS && (
        <ProductsEdit
          startDate={dayjs(calculationVersion.startDate, ISO_DATE_FORMAT)}
          totalParticipantsNumber={totalParticipants}
          paidParticipantsNumber={calculationVersion.participantsNumber.numberOfPaid}
          currency={calculationVersion.currency ?? undefined}
          margin={calculationVersion.margin}
          products={calculationVersion.products}
          availableProducts={products}
          errors={productCurrencyErrors}
          prices={prices}
          isCustomer={isCustomer}
          onChange={handleChange}
          onReplaceProduct={handleReplaceProduct}
        />
      )}
      {mode === Mode.PLANE_TICKETS && (
        <ProductsEdit
          startDate={dayjs(calculationVersion.startDate, ISO_DATE_FORMAT)}
          totalParticipantsNumber={totalParticipants}
          paidParticipantsNumber={calculationVersion.participantsNumber.numberOfPaid}
          currency={calculationVersion.currency ?? undefined}
          margin={calculationVersion.margin}
          products={calculationVersion.products}
          availableProducts={planeTicketsOnly(products)}
          errors={productCurrencyErrors}
          prices={prices}
          isCustomer={isCustomer}
          onChange={handleChange}
          onReplaceProduct={handleReplaceProduct}
        />
      )}
      {mode === Mode.A_AND_O && (
        <ProductsEdit
          startDate={dayjs(calculationVersion.startDate, ISO_DATE_FORMAT)}
          totalParticipantsNumber={totalParticipants}
          paidParticipantsNumber={calculationVersion.participantsNumber.numberOfPaid}
          currency={calculationVersion.currency ?? undefined}
          margin={calculationVersion.margin}
          products={calculationVersion.products}
          availableProducts={aAndOOnly(products)}
          errors={productCurrencyErrors}
          prices={prices}
          isCustomer={isCustomer}
          onChange={handleChange}
          onReplaceProduct={handleReplaceProduct}
        />
      )}
      {productCurrencyErrors.length <= 0 && currencyError && (
        <Grid size={12}>
          <Typography variant="body2" color="error">
            Nie można utworzyć pakietu. Wybierz produkty i sprawdź zgodność waluty.
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

export default CalculationVersionEdit;
